import { Button, Flex, Text } from '@chakra-ui/react';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { EventType } from '@lib/types';

import { ENTER_ROOM_TIME_OFFSET } from '../../constants';
import { TEXT_COLOR } from '../../layouts/EventLayout';

import EventTimeCounter from './EventTimeCounter';
import useEventStatus from './useEventStatus';

type Props = {
  event: EventType;
};

const EventStatusWrapper = ({ children }: any) => {
  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      bg="#f1f1f1"
      mt={{ base: '30px', md: '15px' }}
      padding="15px"
    >
      {children}
    </Flex>
  );
};

const EventStatus = ({ event }: Props) => {
  const { t } = useTranslation(['event']);

  const [searchQuery, setSearchQuery] = useState('');

  const { eventStatus, eventTimeOffset, isEventOngoing, isEnabledToEnterRoom } =
    useEventStatus({
      event,
    });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    setSearchQuery(window.location.search);
  }, []);

  if (eventStatus === 'past') {
    return (
      <EventStatusWrapper>
        <Text
          fontSize="26px"
          lineHeight="46px"
          fontWeight="700"
          color="#B2B2B2"
        >
          {t('event:Event is over')}
        </Text>
      </EventStatusWrapper>
    );
  }
  /**
   * For official event is ongoing or it's x minutes before event start and allow user enter room
   */
  if (isEventOngoing || isEnabledToEnterRoom) {
    // using single button instead of wrapping with EventStatusWrapper
    return (
      <Button
        w="100%"
        color="#fff"
        size="lg"
        fontSize="26px"
        lineHeight="46px"
        height="76px"
        colorScheme="brand"
        p="15px"
        mt={{ base: '30px', md: '15px' }}
        as="a"
        href={`/s/${event.id}${searchQuery}`}
      >
        {t('event:Enter Room')}
      </Button>
    );
  }

  if (event.ticketingStatus === 'inactive') {
    return (
      <EventStatusWrapper>
        <Text
          fontSize="26px"
          lineHeight="46px"
          fontWeight="700"
          color="#B2B2B2"
        >
          {t('event:Ticket purchase is currently not open')}
        </Text>
      </EventStatusWrapper>
    );
  }

  /**
   * Consider we didn't perform migration in event
   * and the ticketingStatus inactive is in previous check,
   * so we are not check `ticketingStatus` here
   *
   * For official event is upcoming and it's still not event enter time
   */
  if (
    eventTimeOffset &&
    eventStatus === 'upcoming' &&
    eventTimeOffset > ENTER_ROOM_TIME_OFFSET
  ) {
    return (
      <EventStatusWrapper>
        <Text
          fontSize="md"
          lineHeight="1.75"
          fontWeight="500"
          color={TEXT_COLOR}
        >
          {t('event:Starts In')}
        </Text>
        <EventTimeCounter startedAt={event.startedAt} />
      </EventStatusWrapper>
    );
  }

  return null;
};

export default EventStatus;
